import { useState, useEffect, useRef } from 'react'
import { getAllCustomers, createCustomer, getAllProducts, createInvoice, updateInvoice, getInvoice, createEditRequest, canEditInvoice } from '../../api'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import ProcessingOverlay from '../ui/ProcessingOverlay'

const emptyItem = (seq: number) => ({ seq, nama_barang: '', qty: '1', satuan: 'PCS', harga: '', discount: '0', total_harga: 0 })

const calcTotal = (item: any) => {
  const qty = parseFloat(String(item.qty).replace(/,/g, '')) || 0
  const harga = parseFloat(String(item.harga).replace(/,/g, '')) || 0
  const disc = parseFloat(String(item.discount).replace(/,/g, '')) || 0
  return (qty * harga) - disc
}

const fmtInput = (val: string) => {
  const num = val.replace(/,/g, '')
  if (!num || isNaN(Number(num))) return val
  return Number(num).toLocaleString('en-US', { maximumFractionDigits: 0 })
}

const rawNum = (val: string) => val.replace(/,/g, '')

const fmt = (n: number) => {
  if (n === null || n === undefined || isNaN(n)) return '0'
  return Number(n).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// Global cache — loaded once, shared across components
let _customerCache: any[] | null = null
let _productCache: any[] | null = null
let _customerPromise: Promise<any[]> | null = null
let _productPromise: Promise<any[]> | null = null

function loadCustomers(): Promise<any[]> {
  if (_customerCache) return Promise.resolve(_customerCache)
  if (!_customerPromise) {
    _customerPromise = getAllCustomers().then((r: any) => {
      _customerCache = r.data
      return _customerCache!
    })
  }
  return _customerPromise
}

function loadProducts(): Promise<any[]> {
  if (_productCache) return Promise.resolve(_productCache)
  if (!_productPromise) {
    _productPromise = getAllProducts().then((r: any) => {
      _productCache = r.data
      return _productCache!
    })
  }
  return _productPromise
}

export function invalidateCustomerCache() { _customerCache = null; _customerPromise = null }
export function invalidateProductCache() { _productCache = null; _productPromise = null }

function CustomerSearch({ value, address, onChange, allowCreate = true }: {
  value: string
  address: string
  onChange: (name: string, address: string, id: number | null) => void
  allowCreate?: boolean
}) {
  const [query, setQuery] = useState(value)
  const [results, setResults] = useState<any[]>([])
  const [allCustomers, setAllCustomers] = useState<any[]>([])
  const [open, setOpen] = useState(false)
  const [hlIdx, setHlIdx] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => { loadCustomers().then(setAllCustomers) }, [])
  useEffect(() => { setQuery(value) }, [value])

  useEffect(() => {
    if (query.length < 1) { setResults([]); setHlIdx(-1); return }
    const q = query.toLowerCase()
    setResults(allCustomers.filter(c => c.name.toLowerCase().includes(q)).slice(0, 20))
    setHlIdx(-1)
  }, [query, allCustomers])

  useEffect(() => {
    if (hlIdx >= 0 && listRef.current) {
      const item = listRef.current.children[hlIdx] as HTMLElement
      item?.scrollIntoView({ block: 'nearest' })
    }
  }, [hlIdx])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (c: any) => {
    setQuery(c.name)
    setOpen(false)
    setHlIdx(-1)
    onChange(c.name, c.address || '', c.id)
  }

  const handleCreateNew = async () => {
    const res: any = await createCustomer({ name: query, address: '', phone: '', email: '' })
    invalidateCustomerCache()
    loadCustomers().then(setAllCustomers)
    handleSelect(res.data)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (open && results.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHlIdx(i => (i < results.length - 1 ? i + 1 : 0))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHlIdx(i => (i > 0 ? i - 1 : results.length - 1))
      } else if (e.key === 'Enter' && hlIdx >= 0) {
        e.preventDefault()
        handleSelect(results[hlIdx])
      }
      return
    }
    // Dropdown closed: Enter moves to next field
    if (e.key === 'Enter') {
      e.preventDefault()
      const card = (e.target as HTMLElement).closest('.card')
      const inputs = card?.querySelectorAll<HTMLInputElement>('input.form-input, input[type="date"]')
      if (inputs) {
        const arr = Array.from(inputs)
        const idx = arr.indexOf(e.target as HTMLInputElement)
        if (idx >= 0 && idx < arr.length - 1) arr[idx + 1].focus()
      }
    }
  }

  const exactMatch = results.some((r: any) => r.name.toLowerCase() === query.toLowerCase())

  return (
    <div className="search-dropdown" ref={ref}>
      <input
        className="form-input"
        placeholder="Search or create customer..."
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value, address, null); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {open && query.length > 0 && (
        <div className="search-results" ref={listRef} style={{ maxHeight: 240, overflowY: 'auto' }}>
          {results.map((c: any, i: number) => (
            <div key={c.id} className="search-result-item" onMouseDown={() => handleSelect(c)}
              style={{ background: i === hlIdx ? '#e0e7ff' : undefined }}>
              <div style={{ fontWeight: 600 }}>{c.name}</div>
              {c.address && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.address}</div>}
            </div>
          ))}
          {allowCreate && !exactMatch && query.trim() && (
            <div className="search-result-item create" onMouseDown={handleCreateNew}>
              + Create "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ProductSearch({ value, onChange, deptFilter }: {
  value: string
  onChange: (name: string, unit: string, price: string) => void
  deptFilter?: string
}) {
  const [query, setQuery] = useState(value)
  const [results, setResults] = useState<any[]>([])
  const [allProducts, setAllProducts] = useState<any[]>([])
  const [open, setOpen] = useState(false)
  const [hlIdx, setHlIdx] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => { loadProducts().then(setAllProducts) }, [])
  useEffect(() => { setQuery(value) }, [value])

  useEffect(() => {
    if (query.length < 1) { setResults([]); setHlIdx(-1); return }
    const q = query.toLowerCase()
    setResults(allProducts
      .filter(p => (!deptFilter || p.dept_code === deptFilter) && p.name.toLowerCase().includes(q))
      .slice(0, 10))
    setHlIdx(-1)
  }, [query, allProducts, deptFilter])

  useEffect(() => {
    if (hlIdx >= 0 && listRef.current) {
      const item = listRef.current.children[hlIdx] as HTMLElement
      item?.scrollIntoView({ block: 'nearest' })
    }
  }, [hlIdx])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (p: any) => {
    setQuery(p.name)
    setOpen(false)
    setHlIdx(-1)
    onChange(p.name, p.unit || 'PCS', String(p.price || ''))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (open && results.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHlIdx(i => (i < results.length - 1 ? i + 1 : 0))
        return
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHlIdx(i => (i > 0 ? i - 1 : results.length - 1))
        return
      } else if (e.key === 'Enter' && hlIdx >= 0) {
        e.preventDefault()
        handleSelect(results[hlIdx])
        return
      }
    }
    // Dropdown closed or no highlight: Enter moves to next cell
    if (e.key === 'Enter' && !open) {
      e.preventDefault()
      // Find the row's QTY input (next sibling td's input)
      const input = (e.target as HTMLElement).closest('td')?.nextElementSibling?.querySelector('input') as HTMLInputElement
      input?.focus()
      input?.select()
    }
  }

  return (
    <div className="search-dropdown" ref={ref} style={{ width: '100%' }}>
      <input
        style={{ width: '100%', padding: '7px 10px', border: '1.5px solid transparent', borderRadius: 6, fontSize: 13, background: '#f8fafc', outline: 'none' }}
        placeholder="Search product..."
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value, 'PCS', ''); setOpen(true) }}
        onFocus={e => { setOpen(true); (e.target as HTMLInputElement).style.borderColor = '#2563eb'; (e.target as HTMLInputElement).style.background = '#fff' }}
        onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'transparent'; (e.target as HTMLInputElement).style.background = '#f8fafc' }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <div className="search-results" ref={listRef} style={{ maxHeight: 240, overflowY: 'auto' }}>
          {results.map((p: any, i: number) => (
            <div key={p.id} className="search-result-item" onMouseDown={() => handleSelect(p)}
              style={{ background: i === hlIdx ? '#e0e7ff' : undefined }}>
              <div style={{ fontWeight: 600 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                {p.dept_code && <span style={{ marginRight: 8 }}>{p.dept_code}</span>}
                {p.unit} &nbsp;&middot;&nbsp; {Number(p.price) > 0 ? Number(p.price).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 'No price'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function InvoiceForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isEdit = Boolean(id)
  const isBillingUser = user?.role === 'BILLING_USER'
  const tableRef = useRef<HTMLTableElement>(null)

  // Keyboard navigation: Enter moves to next field, Arrow Up/Down moves between rows
  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, rowIdx: number, colIdx: number) => {
    const cols = ['qty', 'harga', 'discount'] // navigable columns
    if (e.key === 'Enter') {
      e.preventDefault()
      if (colIdx < cols.length - 1) {
        // Move to next column in same row
        focusCell(rowIdx, colIdx + 1)
      } else {
        // Last column (discount): move to product name of next row
        if (rowIdx >= items.length - 1) {
          addRow()
        }
        setTimeout(() => focusProductCell(rowIdx + 1), 50)
      }
    } else if (e.key === 'ArrowDown' && rowIdx < items.length - 1) {
      e.preventDefault()
      focusCell(rowIdx + 1, colIdx)
    } else if (e.key === 'ArrowUp' && rowIdx > 0) {
      e.preventDefault()
      focusCell(rowIdx - 1, colIdx)
    }
  }

  const focusCell = (row: number, col: number) => {
    if (!tableRef.current) return
    const tbody = tableRef.current.querySelector('tbody')
    if (!tbody) return
    const tr = tbody.children[row] as HTMLTableRowElement
    if (!tr) return
    // cols map to td indices: qty=2, harga=4, discount=5
    const tdMap = [2, 4, 5]
    const td = tr.children[tdMap[col]] as HTMLTableCellElement
    const input = td?.querySelector('input') as HTMLInputElement
    input?.focus()
    input?.select()
  }

  const focusProductCell = (row: number) => {
    if (!tableRef.current) return
    const tbody = tableRef.current.querySelector('tbody')
    if (!tbody) return
    const tr = tbody.children[row] as HTMLTableRowElement
    if (!tr) return
    // Product name is td index 1
    const td = tr.children[1] as HTMLTableCellElement
    const input = td?.querySelector('input') as HTMLInputElement
    input?.focus()
    input?.select()
  }

  const [canEditDirectly, setCanEditDirectly] = useState(true)
  const [showEditRequestModal, setShowEditRequestModal] = useState(false)
  const [editReason, setEditReason] = useState('')
  const [showCustomerChangeRequest, setShowCustomerChangeRequest] = useState(false)
  const [newCustomerName, setNewCustomerName] = useState('')
  const [customerChangeReason, setCustomerChangeReason] = useState('')

  const [header, setHeader] = useState({
    invoice_date: new Date().toISOString().slice(0, 10),
    customer_name: '',
    customer_address: '',
    customer_id: null as number | null,
    payment_type: 'CREDIT',
    department: 'SIL',
    notes: '',
  })
  const [items, setItems] = useState<any[]>([emptyItem(1)])
  const [saving, setSaving] = useState(false)
  const [savingMessage, setSavingMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (isEdit) {
      getInvoice(id).then((r: any) => {
        const inv = r.data
        setHeader({
          invoice_date: inv.invoice_date,
          customer_name: inv.customer_name,
          customer_address: inv.customer_address || '',
          customer_id: inv.customer_id,
          payment_type: inv.payment_type || 'CASH',
          department: inv.department || 'SIL',
          notes: inv.notes || '',
        })
        setItems(inv.items.map((i: any) => ({
          ...i,
          qty: String(i.qty),
          harga: String(i.harga),
          discount: String(i.discount),
        })))
      })
      if (isBillingUser) {
        canEditInvoice(id).then((r: any) => setCanEditDirectly(r.data.can_edit_directly))
      }
    }
  }, [id]) // eslint-disable-line

  const setItem = (idx: number, field: string, value: string) => {
    setItems(prev => {
      const next = [...prev]
      next[idx] = { ...next[idx], [field]: value }
      next[idx].total_harga = calcTotal(next[idx])
      return next
    })
  }

  const addRow = () => setItems(prev => [...prev, emptyItem(prev.length + 1)])

  const removeRow = (idx: number) =>
    setItems(prev => prev.filter((_: any, i: number) => i !== idx).map((it: any, i: number) => ({ ...it, seq: i + 1 })))

  const grandTotal = items.reduce((s: number, i: any) => s + (parseFloat(i.total_harga) || 0), 0)
  const discountTotal = items.reduce((s: number, i: any) => s + (parseFloat(i.discount) || 0), 0)

  const buildPayload = () => ({
    ...header,
    items: items.map((i: any) => ({
      seq: i.seq,
      nama_barang: i.nama_barang,
      qty: parseFloat(i.qty) || 0,
      satuan: i.satuan,
      harga: parseFloat(i.harga) || 0,
      discount: parseFloat(i.discount) || 0,
    })),
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!header.customer_name.trim()) { setError('Customer name is required'); return }
    setSaving(true)
    setSavingMessage(isEdit ? 'Updating invoice...' : 'Saving invoice...')
    setError('')
    try {
      const payload = buildPayload()
      if (isEdit) {
        if (isBillingUser && !canEditDirectly) {
          setShowEditRequestModal(true)
          setSaving(false)
          return
        }
        await updateInvoice(id, payload)
        sessionStorage.removeItem('invoiceListState')
        navigate('/invoices')
      } else {
        const res: any = await createInvoice(payload)
        sessionStorage.removeItem('invoiceListState')
        navigate(`/invoices/${res.data.id}/print`)
      }
    } catch (err: any) {
      if (err.response?.data?.detail === 'EDIT_REQUEST_REQUIRED') {
        setShowEditRequestModal(true)
      } else {
        setError(err.response?.data?.detail || 'Save failed')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSubmitEditRequest = async () => {
    setSaving(true)
    setSavingMessage('Submitting edit request...')
    try {
      await createEditRequest({
        invoice_id: parseInt(id as string),
        changes: buildPayload(),
        reason: editReason,
      })
      setShowEditRequestModal(false)
      sessionStorage.removeItem('invoiceListState')
      navigate('/invoices')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit request')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmitCustomerChangeRequest = async () => {
    if (!newCustomerName.trim()) return
    setSaving(true)
    setSavingMessage('Submitting change request...')
    try {
      await createEditRequest({
        invoice_id: parseInt(id as string),
        changes: { customer_name: newCustomerName.trim() },
        reason: customerChangeReason || `Request to change customer name to: ${newCustomerName.trim()}`,
      })
      setShowCustomerChangeRequest(false)
      setNewCustomerName('')
      setCustomerChangeReason('')
      alert('Request submitted for admin approval.')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit request')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page">
      {saving && <ProcessingOverlay message={savingMessage} />}
      <div className="page-header">
        <div>
          <div className="page-title">{isEdit ? 'Edit Invoice' : 'New Invoice'}</div>
          <div className="page-subtitle">{isEdit ? 'Update invoice details' : 'Faktur Penjualan Baru'}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSubmit as any}>
            {saving ? 'Saving...' : isEdit ? 'Update Invoice' : '💾 Save & Print'}
          </button>
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* Header card */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 16 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Department *</label>
            <select className="form-input" value={header.department}
              onChange={e => setHeader(h => ({ ...h, department: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLElement).closest('.card')?.querySelectorAll<HTMLInputElement>('input.form-input')[0]?.focus() } }}>
              <option value="SIL">💻 Laptop (SIL)</option>
              <option value="SIH">📱 Mobile (SIH)</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Date / Tanggal</label>
            <input type="date" className="form-input" value={header.invoice_date} required
              onChange={e => setHeader(h => ({ ...h, invoice_date: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLElement).closest('.card')?.querySelectorAll<HTMLInputElement>('input')[1]?.focus() } }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Customer / Pelanggan</label>
            {isEdit && isBillingUser ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="form-input" value={header.customer_name} readOnly
                  style={{ background: '#f8fafc', color: '#64748b', cursor: 'not-allowed', flex: 1 }} />
                <button type="button" className="btn btn-sm btn-secondary"
                  onClick={() => setShowCustomerChangeRequest(true)}
                  style={{ whiteSpace: 'nowrap' }}>Request Change</button>
              </div>
            ) : (
              <CustomerSearch
                value={header.customer_name}
                address={header.customer_address}
                onChange={(name, address, cid) => setHeader(h => ({ ...h, customer_name: name, customer_address: address, customer_id: cid }))}
                allowCreate={!isBillingUser}
              />
            )}
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Address / Alamat</label>
            <input className="form-input" placeholder="Customer address" value={header.customer_address}
              onChange={e => setHeader(h => ({ ...h, customer_address: e.target.value }))}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  const selects = (e.target as HTMLElement).closest('.card')?.querySelectorAll<HTMLSelectElement>('select')
                  if (selects && selects.length > 1) selects[1].focus()
                }
              }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Payment Type</label>
            <select className="form-input" value={header.payment_type}
              onChange={e => setHeader(h => ({ ...h, payment_type: e.target.value }))}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (items.length === 0 || (items.length === 1 && !items[0].nama_barang)) {
                    if (items.length === 0) addRow()
                  }
                  setTimeout(() => focusProductCell(0), 50)
                }
              }}>
              <option value="CREDIT">📋 Credit</option>
              <option value="BANK_TRANSFER">🏦 Bank Transfer</option>
              <option value="CASH">💵 Cash</option>
            </select>
          </div>
        </div>
      </div>

      {/* Items card */}
      <div className="card">
        <table className="items-table" ref={tableRef}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Nama Barang / Item Description</th>
              <th style={{ width: 80 }} className="right">QTY</th>
              <th style={{ width: 70 }}>Unit</th>
              <th style={{ width: 130 }} className="right">Harga / Price</th>
              <th style={{ width: 110 }} className="right">Discount</th>
              <th style={{ width: 140 }} className="right">Total</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item: any, idx: number) => (
              <tr key={idx}>
                <td style={{ color: '#94a3b8', fontWeight: 600, textAlign: 'center' }}>{item.seq}</td>
                <td>
                  <ProductSearch
                    value={item.nama_barang}
                    deptFilter={header.department}
                    onChange={(name, unit, price) => {
                      setItems(prev => {
                        const next = [...prev]
                        next[idx] = { ...next[idx], nama_barang: name, satuan: unit, harga: price }
                        next[idx].total_harga = calcTotal(next[idx])
                        return next
                      })
                    }}
                  />
                </td>
                <td>
                  <input type="number" min="0" step="0.01"
                    style={{ width: '100%', padding: '7px 8px', border: '1.5px solid transparent', borderRadius: 6, fontSize: 13, background: '#f8fafc', outline: 'none', textAlign: 'right' }}
                    value={item.qty}
                    onChange={e => setItem(idx, 'qty', e.target.value)}
                    onKeyDown={e => handleItemKeyDown(e as any, idx, 0)}
                    onFocus={e => { e.target.style.borderColor = '#2563eb'; e.target.style.background = '#fff' }}
                    onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = '#f8fafc' }}
                  />
                </td>
                <td>
                  <input
                    style={{ width: '100%', padding: '7px 8px', border: '1.5px solid transparent', borderRadius: 6, fontSize: 13, background: '#f1f5f9', outline: 'none', color: '#64748b' }}
                    value={item.satuan}
                    disabled
                  />
                </td>
                <td>
                  <input
                    style={{ width: '100%', padding: '7px 8px', border: '1.5px solid transparent', borderRadius: 6, fontSize: 13, background: '#f8fafc', outline: 'none', textAlign: 'right' }}
                    value={fmtInput(String(item.harga))}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^0-9]/g, '')
                      setItem(idx, 'harga', raw)
                    }}
                    onKeyDown={e => handleItemKeyDown(e as any, idx, 1)}
                    onFocus={e => { e.target.style.borderColor = '#2563eb'; e.target.style.background = '#fff' }}
                    onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = '#f8fafc' }}
                  />
                </td>
                <td>
                  <input
                    style={{ width: '100%', padding: '7px 8px', border: '1.5px solid transparent', borderRadius: 6, fontSize: 13, background: '#f8fafc', outline: 'none', textAlign: 'right' }}
                    value={fmtInput(String(item.discount))}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^0-9]/g, '')
                      setItem(idx, 'discount', raw)
                    }}
                    onKeyDown={e => handleItemKeyDown(e as any, idx, 2)}
                    onFocus={e => { e.target.style.borderColor = '#2563eb'; e.target.style.background = '#fff' }}
                    onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = '#f8fafc' }}
                  />
                </td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(item.total_harga)}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button type="button" onClick={() => removeRow(idx)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 18, lineHeight: 1, padding: '2px 6px', borderRadius: 4 }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#cbd5e1')}
                  >×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 12 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}>+ Add Row</button>
        </div>

        <div className="total-bar">
          <div>
            {discountTotal > 0 && (
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                Discount: <span style={{ color: '#dc2626' }}>-{fmt(discountTotal)}</span>
              </div>
            )}
            <div className="grand-total-label">Grand Total</div>
          </div>
          <div className="grand-total-value">{fmt(grandTotal)}</div>
        </div>
      </div>

      {/* Edit Request Modal */}
      {showEditRequestModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 440, padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Submit Edit Request</div>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
              You cannot edit this invoice directly after the creation date.
              Your changes will be submitted for admin approval.
            </div>
            <div className="form-group">
              <label className="form-label">Reason for change</label>
              <textarea className="form-input" rows={3} value={editReason}
                onChange={e => setEditReason(e.target.value)}
                placeholder="Explain why this change is needed..."
                style={{ resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowEditRequestModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={saving} onClick={handleSubmitEditRequest}>
                {saving ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Name Change Request Modal */}
      {showCustomerChangeRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 420, padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Request Customer Name Change</div>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
              Current: <strong>{header.customer_name}</strong>
            </div>
            <div className="form-group">
              <label className="form-label">New Customer Name *</label>
              <CustomerSearch
                value={newCustomerName}
                address=""
                onChange={(name) => setNewCustomerName(name)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Reason</label>
              <textarea className="form-input" rows={2} value={customerChangeReason}
                onChange={e => setCustomerChangeReason(e.target.value)}
                placeholder="Why does the name need to change?"
                style={{ resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setShowCustomerChangeRequest(false); setNewCustomerName(''); setCustomerChangeReason('') }}>Cancel</button>
              <button className="btn btn-primary" disabled={saving || !newCustomerName.trim()} onClick={handleSubmitCustomerChangeRequest}>
                {saving ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
